import React, { useState } from 'react';
import { Loader2, ShieldOff, ArrowLeft, Star, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import ReviewManager from '@/components/admin/ReviewManager';
import ReviewDrawPanel from '@/components/admin/ReviewDrawPanel';
import AdminSectionTabs from '@/components/admin/AdminSectionTabs';

// Reviews and customer feedback are one destination in the sidebar, joined by
// this strip. Each keeps its own route, data and actions.
const SECTION_TABS = [
  { to: '/admin/reviews', label: 'REVIEWS' },
  { to: '/admin/feedback', label: 'CUSTOMER FEEDBACK' },
];

export default function AdminReviews() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const [tab, setTab] = useState('reviews'); // 'reviews' | 'draw'

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-10 md:py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/admin/dashboard"
            className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div>
            <p className="text-primary text-xs font-bold tracking-widest mb-2">PRODUCTS &amp; CATALOG</p>
            <h1 className="text-white font-black text-3xl md:text-4xl uppercase">REVIEW MANAGEMENT</h1>
            <p className="text-white/30 text-sm mt-1">Approve customer submissions or import real reviews from Google / Trustpilot, and run the monthly verified-link prize draw.</p>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 pt-10 pb-10 max-w-6xl mx-auto">
        <AdminSectionTabs tabs={SECTION_TABS} className="mb-6" />
        {/* Reviews / Review Draw tabs */}
        <div className="flex items-center gap-1 border border-white/10 bg-[#111115] p-1 w-full sm:w-fit mb-6">
          <button
            onClick={() => setTab('reviews')}
            className={`flex items-center gap-1.5 px-4 py-2 text-[10px] font-bold tracking-wider transition-colors ${tab === 'reviews' ? 'bg-primary text-white' : 'text-white/50 hover:text-white'}`}
          >
            <Star className="w-3.5 h-3.5" /> REVIEWS
          </button>
          <button
            onClick={() => setTab('draw')}
            className={`flex items-center gap-1.5 px-4 py-2 text-[10px] font-bold tracking-wider transition-colors ${tab === 'draw' ? 'bg-primary text-white' : 'text-white/50 hover:text-white'}`}
          >
            <Trophy className="w-3.5 h-3.5" /> REVIEW DRAW
          </button>
        </div>

        {tab === 'reviews' ? <ReviewManager /> : <ReviewDrawPanel />}
      </section>
    </div>
  );
}